import React, { useEffect, useRef, useState } from 'react';
import {
  FormControl, InputLabel, MenuItem, Select, SelectChangeEvent,
  TextField, Typography,
} from '@mui/material';
import AppFilterGrid, { AppFilterResetButton } from '../layout/AppFilterGrid';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useSelector } from 'react-redux';
import { Departement, Tiers } from '../../models/models';
import TiersAutoC from '../AutoComplete/TiersAutoC';
import SelectDepartement from '../Selects/SelectDepartement';

/**
 * Filtre dédié aux opérations diverses — champs adaptés (tiers tous types, sens,
 * dates, montant). Pas de caisse / mode / nature (sans objet pour une OD).
 * Application automatique : les sélecteurs et dates s'appliquent immédiatement,
 * la saisie du montant est debouncée (400 ms) pour éviter une recherche par frappe.
 */
export interface ODFilterValues {
  tiers: Tiers | null;
  sens: 'C' | 'D' | '';
  date_start: string;
  date_end: string;
  montant: string;
  departement: Departement | null;
}

export const OD_EMPTY_FILTERS: ODFilterValues = {
  tiers: null,
  sens: '',
  date_start: '',
  date_end: '',
  montant: '',
  departement: null,
};

const DEBOUNCE_MS = 400;

interface Props {
  /** Appelé automatiquement à chaque changement de filtre (montant debouncé). */
  onSearch: (values: ODFilterValues) => void;
  /** Appelé au clic sur « Réinitialiser » (vide le filtre + relance). */
  onReset: () => void;
}

const OperationDiverseFilters: React.FC<Props> = ({ onSearch, onReset }) => {
  const { isAllMode } = useSelector((state: any) => state.AppSlice);
  const [draft, setDraft] = useState<ODFilterValues>(OD_EMPTY_FILTERS);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Applique immédiatement (tiers, sens, dates — pas de saisie au clavier continue).
  const applyNow = (patch: Partial<ODFilterValues>) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const next = { ...draft, ...patch };
    setDraft(next);
    onSearch(next);
  };

  // Applique avec debounce (montant — évite une recherche à chaque frappe).
  const applyDebounced = (patch: Partial<ODFilterValues>) => {
    const next = { ...draft, ...patch };
    setDraft(next);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => onSearch(next), DEBOUNCE_MS);
  };

  // Nettoyage du timer au démontage.
  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

  const handleReset = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setDraft(OD_EMPTY_FILTERS);
    onReset();
  };

  return (
    <AppFilterGrid minItemWidth={180}>
      <Typography
        variant="subtitle1"
        sx={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600, color: 'text.secondary' }}
      >
        <FilterListIcon fontSize="small" />
        Filtres des opérations diverses
      </Typography>

      <TiersAutoC label="Tiers" onTiersChange={(t) => applyNow({ tiers: t })} value={draft.tiers} />

      {/* departement — filtre utile seulement en mode "Tous départements" : dans un
          contexte de département actif, tout est déjà scopé dessus. */}
      {isAllMode && (
        <SelectDepartement
          onDepartementChange={(d) => applyNow({ departement: d })}
          selectedDepartement={draft.departement}
          allowAll
        />
      )}

      <FormControl fullWidth size="small">
        <InputLabel id="od-sens-filter">Sens</InputLabel>
        <Select
          labelId="od-sens-filter"
          label="Sens"
          value={draft.sens}
          onChange={(e: SelectChangeEvent) => applyNow({ sens: e.target.value as 'C' | 'D' | '' })}
        >
          <MenuItem value=""><em>Tous</em></MenuItem>
          <MenuItem value="C">Crédit</MenuItem>
          <MenuItem value="D">Débit</MenuItem>
        </Select>
      </FormControl>

      <TextField
        label="Date début"
        type="date"
        size="small"
        fullWidth
        value={draft.date_start}
        onChange={(e) => applyNow({ date_start: e.target.value })}
        InputLabelProps={{ shrink: true }}
      />

      <TextField
        label="Date fin"
        type="date"
        size="small"
        fullWidth
        value={draft.date_end}
        onChange={(e) => applyNow({ date_end: e.target.value })}
        InputLabelProps={{ shrink: true }}
      />

      <TextField
        label="Montant"
        type="number"
        size="small"
        fullWidth
        value={draft.montant}
        onChange={(e) => applyDebounced({ montant: e.target.value })}
        InputProps={{ inputProps: { min: 0 } }}
      />

      <AppFilterResetButton onClick={handleReset} />
    </AppFilterGrid>
  );
};

export default OperationDiverseFilters;
